<template>
  <div class="c-dashboard__card">
    <h2 class="c-dashboard__card__title">
      {{ translations.HeartChallengeTitle }}
    </h2>
    <h3 class="c-dashboard__card__heading">
      {{ translations.HeartChallengeHeading }}
    </h3>
    <p class="c-dashboard__card__body">{{ translations.HeartChallengeBody }}</p>

    <form class="c-dashboard__card__form" @submit.prevent="handleSubmit">
      <div class="c-dashboard__card__form__group">
        <div class="c-dashboard__card__form__field">
          <label for="heart-challenge-distance">{{
            translations.HeartChallengeDistanceLabel
          }}</label>
          <div class="input-wrapper input-wrapper--select">
            <select
              id="heart-challenge-distance"
              v-model="formData.distance"
              @change="clearState"
            >
              <option value=""></option>
              <option
                v-for="dist in HeartChallengeMiles"
                :key="dist.DistanceValue"
                :value="dist.DistanceValue"
              >
                {{ dist.DistanceText }}
              </option>
            </select>
          </div>
        </div>

        <div class="c-dashboard__card__form__field">
          <label for="heart-challenge-zip">{{
            translations.HeartChallengeZipcodeLabel
          }}</label>
          <div class="input-wrapper">
            <input
              type="text"
              maxlength="5"
              id="heart-challenge-zip"
              v-model="formData.zipCode"
              @change="clearState"
            />
          </div>
        </div>

        <div class="c-dashboard__card__form__separator">
          <span>OR</span>
        </div>

        <div class="c-dashboard__card__form__field">
          <label for="heart-challenge-state">{{
            translations.HeartChallengeStateLabel
          }}</label>
          <div class="input-wrapper input-wrapper--select">
            <select
              id="heart-challenge-state"
              v-model="formData.state"
              @change="clearZipAndDistance"
            >
              <option value=""></option>
              <option
                v-for="state in HeartChallengeStates"
                :key="state.title"
                :value="state.code"
                :aria-label="state.title"
              >
                {{ state.title }}
              </option>
            </select>
          </div>
        </div>
      </div>

      <div class="c-dashboard__card__form__action">
        <button
          type="submit"
          class="c-dashboard__card__cta"
          :disabled="!isFormValid"
        >
          {{ translations.HeartChallengeCtaText }}
        </button>
      </div>
    </form>

    <div class="c-dashboard__card__events">
      <div v-if="loading" class="loading">Loading...</div>

      <template v-else>
        <div
          v-if="events.length"
          class="c-dashboard__card__events-grid events-grid"
        >
          <div
            v-for="(event, index) in events"
            :key="event.id"
            class="event-card"
          >
            <div class="event-card__header">
              <img
                class="event-card__image"
                :src="getEventImagePath(event.name, index)"
                :alt="event.name"
              />
              <div class="event-card__date">
                <span>{{ formatMonth(event.event_date) }}</span>
                {{ formatDay(event.event_date) }}
              </div>
            </div>
            <div class="event-card__body">
              <div class="event-card__info">
                <h3 v-html="event.name"></h3>
                <p>{{ formatFullDate(event.event_date) }}</p>
              </div>
              <a :href="event.greeting_url" class="event-card__cta">
                More Details
              </a>
            </div>
          </div>
        </div>

        <div
          v-if="events.length && events.length > 4"
          class="c-dashboard__card__events-cta"
        >
          <a :href="viewMoreUrl" class="c-dashboard__card__events-cta">
            {{ translations.HeartChallengeEventsCtaText }}
          </a>
        </div>

        <div
          v-if="searchPerformed && events.length === 0"
          class="c-dashboard__card__no-events"
        >
          <h3>{{ translations.HeartChallengeNoEventsText }}</h3>
        </div>
      </template>
    </div>
  </div>
</template>

<script>
export default {
  name: "EventSearch",

  props: {
    translations: {
      type: Object,
      required: true,
    },
    HeartChallengeMiles: {
      type: Array,
      required: true,
    },
    HeartChallengeStates: {
      type: Array,
      required: true,
    },
    apiKey: {
      type: String,
      required: true,
    },
    baseUrl: {
      type: String,
      required: true,
    },
  },

  data() {
    return {
      formData: {
        zipCode: "",
        distance: "",
        state: "",
      },
      events: [],
      loading: false,
      searchPerformed: false,
    };
  },

  computed: {
    viewMoreUrl() {
      const baseUrl = "/events";
      if (this.formData.state) {
        return `${baseUrl}?state=${encodeURIComponent(this.formData.state)}`;
      } else if (this.formData.zipCode && this.formData.distance) {
        return `${baseUrl}?zipcode=${encodeURIComponent(
          this.formData.zipCode
        )}&distance=${encodeURIComponent(this.formData.distance)}`;
      }
      return baseUrl;
    },
    isFormValid() {
      // Return true if either:
      // 1. State is selected OR
      // 2. Both zipCode and distance are filled out
      return (
        this.formData.state !== "" ||
        (this.formData.zipCode !== "" && this.formData.distance !== "")
      );
    },
  },

  methods: {
    // Add this new method to determine the image prefix based on event name
    getImagePrefix(eventName) {
      const nameLower = eventName.toLowerCase();
      if (nameLower.includes("cyclenation")) {
        return "cyclenation_event_card";
      } else if (nameLower.includes("field day")) {
        return "field_day_event_card";
      }
      return "heartwalk_event_card";
    },

    // Add this method to get the image number (1, 2, or 3)
    getImageNumber(index) {
      // Use modulo to cycle through 1, 2, 3
      return (index % 3) + 1;
    },

    // Add this method to generate the full image path
    getEventImagePath(eventName, index) {
      const prefix = this.getImagePrefix(eventName);
      const imageNumber = this.getImageNumber(index);
      return `/-/media/Project/WBWB/Dashboard-Page/${prefix}_${imageNumber}.jpg`;
    },

    clearState() {
      this.formData.state = "";
    },

    clearZipAndDistance() {
      this.formData.zipCode = "";
      this.formData.distance = "";
    },

    async handleSubmit() {
      if (!this.isFormValid) return;

      this.loading = true;
      let params = {
        v: "1.0",
        api_key: this.apiKey,
        response_format: "json",
        list_page_offset: 0,
        list_page_size: 5,
        list_sort_column: "event_date",
        list_ascending: "true",
      };

      if (this.formData.zipCode && this.formData.distance) {
        params = {
          ...params,
          method: "getTeamraisersByDistance",
          starting_postal: this.formData.zipCode,
          search_distance: this.formData.distance,
        };
      } else if (this.formData.state) {
        params = {
          ...params,
          method: "getTeamraisersByInfo",
          state: this.formData.state,
        };
      }

      try {
        const queryString = new URLSearchParams(params).toString();
        const response = await fetch(`${this.baseUrl}?${queryString}`);
        const data = await response.json();

        if (
          data.getTeamraisersResponse &&
          data.getTeamraisersResponse.teamraiser
        ) {
          this.events = Array.isArray(data.getTeamraisersResponse.teamraiser)
            ? data.getTeamraisersResponse.teamraiser
            : [data.getTeamraisersResponse.teamraiser];
          this.searchPerformed = true;
        } else {
          this.events = [];
        }
      } catch (error) {
        console.error("API call failed:", error);
        this.events = [];
        console.log(
          "this.events api fail",
          this.events,
          "length",
          this.events.length
        );
        this.searchPerformed = true;
      } finally {
        this.loading = false;
      }
    },

    formatMonth(dateString) {
      return new Date(dateString)
        .toLocaleString("en-US", { month: "short" })
        .toUpperCase();
    },

    formatDay(dateString) {
      return new Date(dateString).getDate();
    },

    formatFullDate(dateString) {
      return new Date(dateString).toLocaleDateString("en-US", {
        weekday: "long",
        month: "long",
        day: "numeric",
        year: "numeric",
      });
    },
  },
};
</script>

<style scoped>
.loading {
  text-align: center;
  padding: 2rem;
}
.c-dashboard__card__form__action:before,
.c-dashboard__card__form__action:after {
  display: none;
}
button:disabled {
  background: #222;
  cursor: not-allowed;
}
.events-grid .event-card:nth-child(5) {
  display: none;
}
</style>



// WEBPACK FOOTER //
// src/app/components/wbwb/wbwb-event-search.vue